<template>
    <dialog
        id="recording-test-dialog"
        ref="dialogElement"
        data-testid="recording-test-dialog"
        class="focus-visible:outline-0 bg-white md:p-16 p-4 rounded-xl text-valence-grey-800">
        <div class="flex flex-col gap-4 text-center items-center max-w-prose">
            <h2 class="title-subsection font-bold">{{ errorMessage.title }}</h2>
            <template v-if="error">
                <p class="body-small max-w-xs text-center text-red-400" v-text="errorMessage.body"></p>
            </template>
            <div v-if="loading" class="text-gray-400 flex justify-center p-4">
                <div class="h-6 w-6"><LoadingSpinner /></div>
            </div>
            <div v-show="!loading && shouldShowMicTest" class="flex flex-col gap-4">
                <p class="max-w-sm mx-auto body">Can you confirm that you&apos;re using the correct mic. Speak and pause. Do you hear a replay?</p>
                <div class="flex flex-col gap-2 mt-4">
                    <label for="microphone-select" class="w-full body-small text-gray-500">
                        <div class="flex items-center justify-between w-full">
                            Select microphone <span>{{ testingLabel }}</span>
                        </div>
                        <select id="microphone-select" ref="selectElement" v-model="micSelection" class="w-full p-2 bg-[#F1F1F1] rounded-lg" name="microphoneSelect">
                            <template v-for="mic in mics" :key="mic.deviceId">
                                <option :value="mic.deviceId">{{ mic.label }}</option>
                            </template>
                        </select>
                    </label>
                </div>
                <div class="flex flex-col justify-start text-left gap-2">
                    <div class="body-small text-gray-500">Input level</div>
                    <div role="presentation" class="flex flex-row space-between gap-2">
                        <template v-for="(item, idx) in levelSignifiers" :key="idx">
                            <div class="h-5 w-2 rounded-lg" :class="idx < audioLevel ? 'bg-black' : 'bg-gray-300'"></div>
                        </template>
                    </div>
                </div>
                <div class="mt-4">
                    <BaseButton theme="primary" @click="confirmMicSelection">Use this Mic</BaseButton>
                </div>
            </div>
            <button v-if="!loading" type="button" class="details underline text-gray-600 hover:text-gray-600/80" @click="cancel">{{ dismissText }}</button>
        </div>
    </dialog>
</template>

<script setup>
import { app } from "@microsoft/teams-js";
import { computedAsync } from "@vueuse/core";
import { isInIFrame } from "~vue/utils";
import { logUserInteraction } from "~vue/utils/logUtils";
import { computed, inject, nextTick, onMounted, onUnmounted, ref, watch } from "vue";

import BaseButton from "./components/BaseButton.vue";
import { CHAT_EVENT } from "./events";
import LoadingSpinner from "./icons/LoadingSpinner.vue";

const ERRORS = {
    NO_INPUT_DEVICES: "no_devices",
    NOT_FOUND: "not_found",
    NOT_ALLOWED: "not_allowed",
    CATCH_ALL: "catch_all",
};
const PREFERRED_MIC_STORAGE_KEY = "valence_preferred_microphone";
// How many audio level signifiers to show in the UI
const LEVELS_SIGNIFIERS = 25;
const PSEUDO_MIC_DEVICE_IDS = new Set(["default", "communications"]);

function normalizeMics(devices) {
    const audioInputs = devices.filter((device) => device.kind === "audioinput" && device.deviceId !== "");
    const concreteMics = audioInputs.filter((device) => !PSEUDO_MIC_DEVICE_IDS.has(device.deviceId));
    return concreteMics.length > 0 ? concreteMics : audioInputs;
}

const props = defineProps({
    eventSlug: {
        type: String,
        default: undefined,
    },
    dismissText: {
        type: String,
        default: "Use text instead",
    },
});
const emit = defineEmits(["micSelected", "dismiss"]);
const { emitter } = inject("globalProperties");

const loading = ref(true);
const mics = ref([]);
const micSelection = ref(undefined);
const audioOutputSupported = ref(false);
const error = ref(null);
const mediaStream = ref(null);
const audioLevel = ref(0);
const audioContext = ref(null);
const audioNodes = ref([]);
const pollingIntervalId = ref(null);
const dialogElement = ref(null);
const selectElement = ref(null);

// Creates an Array with N elements, where N = LEVELS_SIGNIFIERS.
// Just used to loop it in the template to draw the signifiers
const levelSignifiers = computed(() => Array.apply(null, Array(LEVELS_SIGNIFIERS)));

const DEFAULT_ERROR_MESSAGE = {
    title: "I'm having trouble hearing you.",
    body: "It looks like your microphone permissions are not turned on in your browser. Please turn them on and try again.",
};
const ERROR_MESSAGES = {
    Office: {
        title: "M365 Copilot does not support microphone access",
        body: "Continue with text only.",
    },
    Outlook: {
        title: "Outlook does not support microphone access",
        body: "Continue with text only.",
    },
    TeamsModern: {
        title: DEFAULT_ERROR_MESSAGE.title,
        body: "It looks like your microphone permissions are not turned on in MS Teams. Please turn them on and try again.",
    },
    [ERRORS.NO_INPUT_DEVICES]: {
        title: DEFAULT_ERROR_MESSAGE.title,
        body: "It looks like there is no microphone connected. Please connect a microphone and try again",
    },
    [ERRORS.NOT_ALLOWED]: DEFAULT_ERROR_MESSAGE,
    [ERRORS.CATCH_ALL]: DEFAULT_ERROR_MESSAGE,
};
const errorMessage = computedAsync(
    async () => {
        const inIFrame = isInIFrame();
        let errorMsgKey = error.value;
        if (inIFrame) {
            app.initialize();
            const context = await app.getContext();
            errorMsgKey = context.app.host.name;
        }
        return ERROR_MESSAGES[errorMsgKey] || DEFAULT_ERROR_MESSAGE;
    },
    { title: null, body: null },
);
const shouldShowMicTest = computed(() => {
    return error.value === null || ![ERRORS.NO_INPUT_DEVICES, ERRORS.NOT_ALLOWED, ERRORS.NOT_FOUND].includes(error.value);
});
const testingLabel = computed(() => {
    const index = mics.value.findIndex((mic) => mic.deviceId === micSelection.value);
    return `${index + 1}/${mics.value.length}`;
});

watch(micSelection, async () => {
    // Recreate the stream and audio playback if microphone selection changes
    await teardown();
    await createAudioPlayback();
});

onMounted(() => {
    if (dialogElement.value) {
        dialogElement.value.addEventListener("cancel", cancel);
    }
    emitter.on(CHAT_EVENT.OPEN_MIC_TEST_DIALOG, open);
});

onUnmounted(() => {
    teardown();
    clearInterval(pollingIntervalId.value);
    if (dialogElement.value) {
        dialogElement.value.removeEventListener("cancel", cancel);
    }
    emitter.off(CHAT_EVENT.OPEN_MIC_TEST_DIALOG, open);
});

async function start() {
    // Enumerating the mics requires having permissions to access the media devices so we prompt once first. This is a noop if the user has already granted permission elsewhere before.
    try {
        micSelection.value = undefined;
        error.value = null;
        mediaStream.value = await requestPermission({ audio: true });
        await teardown(); // Teardown the temporary stream
        await setDeviceInformation();
        // Query for mics at an interval to pick up any microphone that gets connected or disconnected.
        pollingIntervalId.value = setInterval(setDeviceInformation, 2000);
    } catch (e) {
        handleMediaDeviceError(e);
        clearInterval(pollingIntervalId.value);
    }
    nextTick(() => {
        loading.value = false;
        if (selectElement.value) {
            selectElement.value.focus();
        }
    });
}

function handleMediaDeviceError(e) {
    switch (e.name) {
        case "NotAllowedError":
            error.value = ERRORS.NOT_ALLOWED;
            return;
        case "NotFoundError":
            error.value = ERRORS.NOT_FOUND;
            return;
    }
    error.value = ERRORS.CATCH_ALL;
}

async function setDeviceInformation() {
    try {
        const devices = await navigator.mediaDevices.enumerateDevices();
        mics.value = normalizeMics(devices);
        audioOutputSupported.value = devices.some((device) => device.kind === "audiooutput");
        if (mics.value.length === 0) {
            error.value = ERRORS.NO_INPUT_DEVICES;
            return;
        } else {
            error.value = null;
        }

        // Default to saved preference, or first audio input if no selection has been made,
        // or if a selection exists for a mic that no longer exists.
        if (!micSelection.value || !mics.value.find((mic) => mic.deviceId === micSelection.value)) {
            const savedMicId = localStorage.getItem(PREFERRED_MIC_STORAGE_KEY);
            if (savedMicId && mics.value.find((mic) => mic.deviceId === savedMicId)) {
                micSelection.value = savedMicId;
            } else {
                micSelection.value = mics.value[0].deviceId;
            }
        }
    } catch (e) {
        handleMediaDeviceError(e);
    }
}

function requestPermission(constraints = {}) {
    return navigator.mediaDevices.getUserMedia(constraints);
}

async function dismiss() {
    await teardown();
    dialogElement.value.close();
}

async function cancel() {
    logUserInteraction("choose_mic_modal_text_selected", {}, props.eventSlug);
    emit("dismiss");
    await dismiss();
}

async function confirmMicSelection() {
    const mic = mics.value.find((mic) => mic.deviceId === micSelection.value);
    localStorage.setItem(PREFERRED_MIC_STORAGE_KEY, micSelection.value);
    logUserInteraction("choose_mic_modal_mic_selected", {}, props.eventSlug);
    emit("micSelected", mic);
    await dismiss();
}

async function teardown() {
    if (mediaStream.value) {
        mediaStream.value.getAudioTracks().forEach((track) => track.stop());
    }
    audioNodes.value.forEach((node) => node.disconnect());
    if (audioContext.value) {
        await audioContext.value.close();
    }
    audioNodes.value = [];
    audioContext.value = null;
    mediaStream.value = null;
}

function open() {
    dialogElement.value.showModal();
    start();
    logUserInteraction("choose_mic_modal_viewed", {}, props.eventSlug);
}

async function createAudioPlayback() {
    mediaStream.value = await requestPermission({
        audio: { deviceId: micSelection.value },
    });
    audioContext.value = new AudioContext();
    const source = audioContext.value.createMediaStreamSource(mediaStream.value);
    const delay = audioContext.value.createDelay();
    delay.delayTime.value = 300;
    const analyser = audioContext.value.createAnalyser();
    analyser.fftSize = 256;
    // Store these nodes for proper teardown elsewhere
    audioNodes.value.push(source, delay, analyser);
    const bufferLength = analyser.frequencyBinCount;
    const levelsArray = new Uint8Array(bufferLength);
    source.connect(analyser);
    if (audioOutputSupported.value) {
        source.connect(delay);
        delay.connect(audioContext.value.destination);
        logUserInteraction("choose_mic_audio_playback", {}, props.eventSlug);
    }
    const getAudioLevels = () => {
        analyser.getByteFrequencyData(levelsArray);

        // Calculate the average of the frequency data
        let sum = 0;
        for (let i = 0; i < bufferLength; i++) {
            sum += levelsArray[i];
        }
        const average = sum / bufferLength;
        audioLevel.value = Math.round((average / 255) * LEVELS_SIGNIFIERS);
        // Run at levels analysis on a browser-controlled loop
        requestAnimationFrame(getAudioLevels);
    };
    getAudioLevels();
}
</script>

<style type="postcss">
dialog#recording-test-dialog::backdrop {
    background-color: #000;
    opacity: 0.4;
}
</style>
